import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import api from '../../utils/api';
import { Users, UserCheck, UserX, ShieldOff, Download, Calendar, TrendingUp, BarChart3 } from 'lucide-react';

export default function AdminReports() {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchReport = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    api.get(`/users/admin/user-report?${params}`)
      .then(r => setReport(r.data))
      .catch(err => toast.error(err.response?.data?.message || 'Failed to load report'))
      .finally(() => setLoading(false));
  }, [from, to]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const exportCsv = () => {
    if (!report?.signupTrend?.length) return toast.error('Nothing to export');
    const rows = [['Date', 'Signups'], ...report.signupTrend.map(r => [r._id, r.count])];
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `user-signup-report-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const maxCount = report?.signupTrend?.length ? Math.max(...report.signupTrend.map(r => r.count)) : 0;

  return (
    <DashboardLayout>
      <h1 className="page-title">Reports</h1>
      <p className="page-subtitle">Platform-wide user growth and income reporting</p>

      {/* Date filter */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Calendar size={14} /> From</label>
            <input type="date" className="form-control" value={from} onChange={e => setFrom(e.target.value)} />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Calendar size={14} /> To</label>
            <input type="date" className="form-control" value={to} onChange={e => setTo(e.target.value)} />
          </div>
          <button className="btn btn-outline" onClick={exportCsv}><Download size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} /> Export CSV</button>
        </div>
      </div>

      {loading ? (
        <div className="loader-wrap"><div className="loader"></div></div>
      ) : report && (
        <>
          <div className="wallet-grid" style={{ marginBottom: 24 }}>
            <div className="wallet-card">
              <div style={{ marginBottom: 6, color: '#3b82f6', display: 'flex', justifyContent: 'center' }}><Users size={24} /></div>
              <div className="wallet-label">Total Users</div>
              <div className="wallet-value" style={{ color: '#3b82f6' }}>{report.totalUsers}</div>
            </div>
            <div className="wallet-card">
              <div style={{ marginBottom: 6, color: '#22c55e', display: 'flex', justifyContent: 'center' }}><UserCheck size={24} /></div>
              <div className="wallet-label">Active (Package)</div>
              <div className="wallet-value" style={{ color: '#22c55e' }}>{report.activeUsers}</div>
            </div>
            <div className="wallet-card">
              <div style={{ marginBottom: 6, color: '#f59e0b', display: 'flex', justifyContent: 'center' }}><UserX size={24} /></div>
              <div className="wallet-label">Inactive</div>
              <div className="wallet-value" style={{ color: '#f59e0b' }}>{report.inactiveUsers}</div>
            </div>
            <div className="wallet-card">
              <div style={{ marginBottom: 6, color: '#ef4444', display: 'flex', justifyContent: 'center' }}><ShieldOff size={24} /></div>
              <div className="wallet-label">Blocked</div>
              <div className="wallet-value" style={{ color: '#ef4444' }}>{report.blockedUsers}</div>
            </div>
          </div>

          {/* Signup trend */}
          <div className="card" style={{ marginBottom: 24 }}>
            <div className="card-header">
              <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}><TrendingUp size={18} /> Signup Trend</h3>
            </div>
            {report.signupTrend.length === 0 ? (
              <div className="empty-state"><div className="empty-icon"><TrendingUp size={40} /></div><h3>No signups in this range</h3></div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 160, overflowX: 'auto', padding: '8px 4px' }}>
                {report.signupTrend.map(d => (
                  <div key={d._id} title={`${d._id}: ${d.count}`} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 28 }}>
                    <div style={{
                      width: 18,
                      height: maxCount ? `${(d.count / maxCount) * 120}px` : '4px',
                      minHeight: 4,
                      background: 'linear-gradient(180deg, var(--primary), var(--primary-dark))',
                      borderRadius: '4px 4px 0 0'
                    }} />
                    <div style={{ fontSize: '0.62rem', color: 'var(--text-muted)', marginTop: 4, transform: 'rotate(-40deg)', whiteSpace: 'nowrap' }}>{d._id.slice(5)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Links to other reports */}
          <div className="grid-2" style={{ gap: 16 }}>
            <Link to="/admin/income" className="card" style={{ display: 'flex', alignItems: 'center', gap: 14, textDecoration: 'none' }}>
              <BarChart3 size={28} color="var(--primary)" />
              <div>
                <div style={{ fontWeight: 700, color: 'var(--text-white)' }}>Income Report</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>ROI, Direct, Level, Royalty & Reward payouts</div>
              </div>
            </Link>
            <Link to="/admin/team" className="card" style={{ display: 'flex', alignItems: 'center', gap: 14, textDecoration: 'none' }}>
              <Users size={28} color="#3b82f6" />
              <div>
                <div style={{ fontWeight: 700, color: 'var(--text-white)' }}>Business Report</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Team size & business leaderboard</div>
              </div>
            </Link>
          </div>
        </>
      )}
    </DashboardLayout>
  );
}